import { h } from 'vue';

import type { ComponentProps } from '@nzyme/vue-utils/types.js';
import type { ChatEvent } from '@sigma/chat-common/ChatEvent.js';
import { isPatientVisibleEvent } from '@sigma/chat-common/utils/isPatientVisibleEvent.js';
import type { ChatEventProps } from '@sigma/client/chat/defineChatEventProps.js';

import ChatAgentBusy from './ChatAgentBusy.vue';
import ChatAgentMessage from './ChatAgentMessage.vue';
import ChatAgentQuestion from './ChatAgentQuestion.vue';
import ChatEnd from './ChatEnd.vue';
import ChatPatientAnswer from './ChatPatientAnswer.vue';
import ChatPatientMessage from './ChatPatientMessage.vue';
import ChatProfileDataRequest from './ChatProfileDataRequest.vue';
import ChatProfileDataSent from './ChatProfileDataSent.vue';
import ChatStaffJoin from './ChatStaffJoin.vue';
import ChatStaffMessage from './ChatStaffMessage.vue';
import ChatSubscriptionRequest from './ChatSubscriptionRequest.vue';
import ChatSubscriptionStart from './ChatSubscriptionStart.vue';

export function ChatMessage(props: ChatEventProps<ChatEvent>) {
    // AGENT_BUSY is a transient typing indicator - it renders only while it is the
    // last event and is deliberately excluded from the visibility predicate.
    if (props.event.type === 'AGENT_BUSY') {
        const isLast = props.chat.events.at(-1) === props.event;
        if (!isLast) {
            return null;
        }
        return h(ChatAgentBusy, props as ComponentProps<typeof ChatAgentBusy>);
    }

    if (!isPatientVisibleEvent(props.event)) {
        return null;
    }

    switch (props.event.type) {
        case 'AGENT_MESSAGE':
            return h(ChatAgentMessage, props as ComponentProps<typeof ChatAgentMessage>);
        case 'AGENT_QUESTION':
            return h(ChatAgentQuestion, props as ComponentProps<typeof ChatAgentQuestion>);
        case 'CHAT_END':
            return h(ChatEnd, props as ComponentProps<typeof ChatEnd>);
        case 'PATIENT_ANSWER':
            return h(ChatPatientAnswer, props as ComponentProps<typeof ChatPatientAnswer>);
        case 'PATIENT_MESSAGE':
            return h(ChatPatientMessage, props as ComponentProps<typeof ChatPatientMessage>);
        case 'PROFILE_DATA_REQUEST':
            return h(ChatProfileDataRequest, props as ComponentProps<typeof ChatProfileDataRequest>);
        case 'PROFILE_DATA_SENT':
            return h(ChatProfileDataSent, props as ComponentProps<typeof ChatProfileDataSent>);
        case 'STAFF_JOIN':
            return h(ChatStaffJoin, props as ComponentProps<typeof ChatStaffJoin>);
        case 'STAFF_MESSAGE':
            return h(ChatStaffMessage, props as ComponentProps<typeof ChatStaffMessage>);
        case 'SUBSCRIPTION_REQUEST':
            return h(ChatSubscriptionRequest, props as ComponentProps<typeof ChatSubscriptionRequest>);
        case 'SUBSCRIPTION_START':
            return h(ChatSubscriptionStart, props as ComponentProps<typeof ChatSubscriptionStart>);
        default:
            return null;
    }
}
